import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { handleOnSettled } from '@utils/formHelpers';
import { localStore } from '@utils/localStore';
import { useSignIn } from '@services/auth/loginUser';
import { useErrors } from '@hooks/errors';
import { isSlugCorrect, updateSlug } from '@hooks/slug/helpers';
import { LoginFormValues } from './types';
import { defaultValues, formSchema } from './constants';
import { FormProps } from '@forms/types';
import { SignInData, SignInResp } from '@services/auth/types';
import { createSignInExchange } from '@services/auth/signInExchange';
import { getAccess } from '@hooks/access/helpers';
import { isRunningAsPwa } from '@utils/pwa';
import { useLogout } from '@hooks/useLogout';
import { reportAuthError } from '@services/auth/report';
import i18n from 'i18next';
import { trackLogin } from './helpers';
import { useState } from 'react';

type LoginFormProps = FormProps<{ keepLocation?: boolean }>;

export const useLoginForm = ({ successHandler, keepLocation = false }: LoginFormProps) => {
  const handleErrors = useErrors();
  const logout = useLogout();
  const [isFinalizing, setIsFinalizing] = useState(false);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const { setError } = form;
  const updateAuthData = async ({ id, primaryRole, tenant }: SignInData): Promise<boolean> => {
    const item = localStore.getItem(`app-${!tenant?.slug ? 'admin' : 'user'}`);
    const parsed = typeof item === 'string' ? (JSON.parse(item) as Record<string, string>) : item;
    if (!tenant?.slug) {
      localStore.updateItem('app-admin', { ...parsed, id });
      return true;
    }
    const access = getAccess(primaryRole, []);
    if (access.fieldUser || isRunningAsPwa() || isSlugCorrect(tenant.slug)) {
      localStore.updateItem('app-user', { ...parsed, id });
      if (access.fieldUser) localStore.updateItem('app-foreman', { enabled: true });
      return true;
    }
    const { data } = await createSignInExchange();
    localStore.removeItem('app-user');
    updateSlug(tenant.slug, data.code, keepLocation);
    return false;
  };

  const callback = (resp: SignInResp) => {
    const errors = resp.errors || [];
    handleOnSettled<SignInResp>(
      resp,
      (data) => {
        const signInData = data as unknown as SignInData;
        trackLogin(signInData);
        setIsFinalizing(true);
        updateAuthData(signInData)
          .then((shouldNavigate) => {
            if (shouldNavigate) successHandler();
          })
          .catch((error: unknown) => {
            reportAuthError('finalize', error);
            logout(signInData.tenant?.slug ? 'user' : 'admin', 'finalizeFailed');
            setIsFinalizing(false);
            setError('root', {
              message: i18n.t('pages.login.finalizeError', { ns: 'app' }),
            });
          });
      },
      () =>
        handleErrors({ root: errors?.[0] || '', ...errors }, (key, message) =>
          setError(key as keyof LoginFormValues, { message }),
        ),
    );
  };

  const { mutate, isLoading } = useSignIn({ callback });

  const onSubmit = async (values: LoginFormValues) => {
    mutate(values);
  };

  return {
    form,
    onSubmit,
    locked: isLoading || isFinalizing,
  };
};
